import cn from 'classnames';
import { lazy, Suspense } from 'react';
import { FlagUnitPopperProps } from 'components/FlagUnitPopper';
import PublisherPlayer from 'components/PublisherPlayer';
import UnloadingAudio from 'components/UnloadingAudio';
import useSanitizedUnitAudio from 'hooks/useSanitizedUnitAudio';
import { useUnit, useFlagUnit } from 'state/unit';
import { renderComponentOrNode } from 'utils/react';
import { removeSpecialCharacters } from 'utils/string';
import { usePlayer } from '../context/PlayerContext';
import { PlayerSizeProvider } from '../context/PlayerSizeContext';
import { PlayerElements } from '../types';
import styles from './PlayerUi.module.scss';

const FlagUnitView = lazy(() => import('components/FlagUnitView'));

export interface PlayerUiProps
  extends Omit<PlayerElements, 'visualization'>,
    Pick<FlagUnitPopperProps, 'onFlagged'> {
  className?: string;
  visualization?: PlayerElements['visualization'];
}

const PlayerUi: React.FC<PlayerUiProps> = ({
  className,
  controls,
  visualization,
  onFlagged,
}) => {
  const unit = useUnit();
  const unitId = removeSpecialCharacters(unit?.resultId);

  const { canFlag, displayFlaggedState } = useFlagUnit();

  const sanitizedSrc = useSanitizedUnitAudio();
  const { setAudioElement } = usePlayer();

  return (
    <>
      <PlayerSizeProvider>
        <div className={cn(styles.root, className)} id={unitId}>
          {canFlag ? (
            <Suspense fallback={null}>
              <FlagUnitView
                className={styles.header}
                triggerClassName={styles.trigger}
                onFlagged={onFlagged}
                displayFlagged={displayFlaggedState}
                containerSelector={'#' + unitId}
              />
            </Suspense>
          ) : null}
          {renderComponentOrNode(visualization)}
          {!displayFlaggedState ? renderComponentOrNode(controls) : null}
          <UnloadingAudio
            crossOrigin="anonymous"
            key={sanitizedSrc}
            preload="none"
            ref={!displayFlaggedState ? setAudioElement : null}
            src={sanitizedSrc}
          />
        </div>
      </PlayerSizeProvider>
      {unit?.episode.audioPlaybackMethod === 'headlinerWithOriginal' ? (
        <PublisherPlayer />
      ) : null}
    </>
  );
};

export default PlayerUi;
